import React from "react";
import { Link } from "react-router-dom";
import { Nav } from "@/components/layout/Nav";
import { Footer } from "@/components/layout/Footer";
import { PageSEO } from "@/components/seo/PageSEO";
import { SITE_URL } from "@/utils/seo";
import { ArrowRight, CheckCircle, ShoppingBag } from "lucide-react";

const SITE = SITE_URL;

const categories = [
  {
    heading: "Strollers",
    desc: "Single and double strollers from Baby Jogger, BOB, Nuna, UPPAbaby and Zoe. Sanitized and inspected before every rental.",
    link: "/strollers",
    items: ["Single strollers from $20/day", "Double strollers from $28/day", "Lightweight & all-terrain options"],
  },
  {
    heading: "Cribs & Sleep Gear",
    desc: "Full-size cribs and pack 'n plays with fresh laundered linens. We set up and take down at your property.",
    link: "/gear",
    items: ["Full-size portable crib", "Pack 'n Play with bassinet", "Fresh linens included with every rental"],
  },
  {
    heading: "Feeding Equipment",
    desc: "High chairs, booster seats and the Baby Brezza Formula Pro for easy, mess-free feeding on vacation.",
    link: "/gear",
    items: ["High chairs", "Booster seats", "Baby Brezza Formula Pro"],
  },
  {
    heading: "Bath & Play",
    desc: "Baby bathtubs, bouncers, swings and activity items to keep little ones comfortable between park days.",
    link: "/gear",
    items: ["Baby bathtubs", "Bouncers & swings", "Activity seats"],
  },
];

const faqs = [
  {
    q: "Do you deliver baby equipment to Disney World resorts?",
    a: "Yes. We deliver cribs, strollers, high chairs and other baby gear to every Walt Disney World resort. All Disney resort deliveries require an in-person lobby meeting — Disney does not allow third-party vendors to leave items at bell services.",
  },
  {
    q: "What baby gear do you rent near Disney World?",
    a: "We rent strollers, full-size cribs, pack 'n plays, high chairs, booster seats, Baby Brezza Formula Pro, baby bathtubs, bouncers, swings and more. Browse our full catalog to see everything available.",
  },
  {
    q: "Is baby gear delivery free near Disney World?",
    a: "Standard delivery is free to Disney resorts, Universal Orlando, Kissimmee vacation homes and area hotels. No early-delivery surcharges.",
  },
  {
    q: "Can I rent multiple baby gear items together?",
    a: "Yes. Most families bundle a stroller, crib and one or two additional items. Add everything you need to your cart at checkout — delivery is a single trip and there is no per-item delivery charge.",
  },
  {
    q: "Do I need to sanitize the equipment myself?",
    a: "No. All equipment arrives sanitized and inspection-ready. Cribs include fresh laundered linens. You do not need to bring cleaning supplies for any rental item.",
  },
  {
    q: "Can I rent baby gear for a vacation home or Airbnb near Disney?",
    a: "Yes. We deliver to vacation homes, Airbnb and VRBO properties throughout the Kissimmee, Celebration, Reunion and Lake Buena Vista areas — typically within a 20-minute drive of Disney.",
  },
];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
    { "@type": "ListItem", position: 2, name: "Baby Gear", item: `${SITE}/gear` },
    { "@type": "ListItem", position: 3, name: "Baby Gear Rental Disney World", item: `${SITE}/baby-gear-rental-disney-world` },
  ],
};

export default function BabyGearRentalDisney() {
  return (
    <>
      <PageSEO
        title="Baby Gear Rental Near Disney World | Equipment Rental Orlando"
        description="Rent cribs, strollers, high chairs, Baby Brezza and more near Walt Disney World. Free delivery to Disney resorts, Universal, vacation homes and Orlando hotels. Enchanted Gear Rentals."
        canonical={`${SITE}/baby-gear-rental-disney-world`}
        structuredData={[breadcrumbSchema, faqSchema]}
      />
      <Nav />
      <main className="min-h-screen" style={{ background: "#040A0F" }}>

        {/* Hero */}
        <section className="pt-28 pb-16 text-center" style={{ background: "linear-gradient(180deg, #060f18 0%, #040A0F 100%)" }}>
          <div className="container max-w-3xl">
            <nav aria-label="Breadcrumb" className="text-xs text-[#B9C8D5] mb-4">
              <Link to="/" className="hover:text-[#FFD166]">Home</Link>
              <span className="mx-2 opacity-40">/</span>
              <Link to="/gear" className="hover:text-[#FFD166]">Baby Gear</Link>
              <span className="mx-2 opacity-40">/</span>
              <span className="text-[#FFD166]">Baby Gear Rental Near Disney World</span>
            </nav>
            <h1 className="font-serif text-4xl md:text-5xl text-[#F6F1E4] mb-5">
              Baby Equipment Rental Near Disney World
            </h1>
            <p className="text-[#B9C8D5] text-lg max-w-2xl mx-auto">
              Travel lighter. Rent everything you need — cribs, strollers, high chairs, formula makers and more — delivered free to your Disney resort, vacation home or Orlando hotel. No car seats, no checked bags, no hassle.
            </p>
            <div className="mt-7 flex gap-4 justify-center flex-wrap">
              <Link to="/#trip-setup"><button className="btn-primary" data-testid="babygear-cta-top">Check Availability <ArrowRight className="inline w-4 h-4 ml-1" /></button></Link>
              <Link to="/gear"><button className="btn-ghost">Browse All Gear</button></Link>
            </div>
          </div>
        </section>

        {/* Gear categories */}
        <section className="container py-14">
          <h2 className="font-serif text-3xl text-[#F6F1E4] mb-3">Baby gear we rent near Disney World</h2>
          <p className="text-[#B9C8D5] mb-10 max-w-2xl">Everything is cleaned, inspected and delivered — so your family lands ready to enjoy the magic, not unpack boxes.</p>
          <div className="grid sm:grid-cols-2 gap-6">
            {categories.map((c) => (
              <div key={c.heading} className="rounded-xl bg-[#0A1825] border border-[#FFD166]/15 p-6">
                <ShoppingBag className="w-5 h-5 text-[#FFD166] mb-3" />
                <h3 className="font-semibold text-[#F6F1E4] text-lg mb-2">{c.heading}</h3>
                <p className="text-sm text-[#B9C8D5] mb-4">{c.desc}</p>
                <ul className="space-y-1.5 mb-4">
                  {c.items.map((item) => (
                    <li key={item} className="flex items-center gap-2 text-sm text-[#B9C8D5]">
                      <CheckCircle className="w-3.5 h-3.5 text-emerald-400 shrink-0" />{item}
                    </li>
                  ))}
                </ul>
                <Link to={c.link} className="text-sm text-[#FFD166] hover:underline inline-flex items-center gap-1">
                  Browse {c.heading} <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            ))}
          </div>
        </section>

        {/* Why rent vs bring */}
        <section className="container py-10">
          <div className="max-w-3xl">
            <h2 className="font-serif text-3xl text-[#F6F1E4] mb-4">Why Orlando families rent baby gear instead of packing it</h2>
            <p className="text-[#B9C8D5] mb-6">
              Flying with a stroller and crib means oversized baggage fees, airport wrestling, and hoping your gear survives the cargo hold. Renting locally means you walk off the plane with carry-ons only — and everything is waiting at your resort, set up and ready.
            </p>
            <ul className="space-y-3">
              {[
                "Skip checked-bag fees for bulky baby items",
                "No assembly — we set up cribs and gear at your property",
                "Hospital-grade sanitized before every rental",
                "Free standard delivery across the greater Orlando area",
                "Return at pickup — no return shipping or storage needed",
              ].map((item) => (
                <li key={item} className="flex items-start gap-3 text-[#B9C8D5] text-sm">
                  <CheckCircle className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />{item}
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Service area */}
        <section className="container py-10">
          <div className="max-w-2xl rounded-2xl bg-[#0A1825] border border-[#FFD166]/20 p-7">
            <h2 className="font-serif text-2xl text-[#F6F1E4] mb-4">Baby gear delivery area — Orlando &amp; Disney World</h2>
            <p className="text-[#B9C8D5] text-sm mb-4">We serve the full Central Florida vacation zone, including Walt Disney World, Universal Orlando, Kissimmee, Lake Buena Vista, Celebration, Reunion, Bonnet Creek and surrounding vacation rental communities.</p>
            <div className="grid sm:grid-cols-2 gap-2 text-sm text-[#B9C8D5]">
              {["All Walt Disney World resorts", "Universal Orlando hotels", "Kissimmee vacation homes", "Airbnb & VRBO near Disney", "Lake Buena Vista hotels", "Bonnet Creek resorts", "Celebration & Reunion", "Disney Springs-area hotels"].map((a) => (
                <div key={a} className="flex items-center gap-2"><CheckCircle className="w-3.5 h-3.5 text-[#FFD166] shrink-0" />{a}</div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="container py-10">
          <h2 className="font-serif text-3xl text-[#F6F1E4] mb-8">Baby gear rental FAQ — Disney World &amp; Orlando</h2>
          <div className="space-y-4 max-w-3xl">
            {faqs.map((f) => (
              <details key={f.q} className="group rounded-xl bg-[#0A1825] border border-[#FFD166]/10 p-5">
                <summary className="font-semibold text-[#F6F1E4] cursor-pointer list-none flex justify-between items-center gap-3">
                  {f.q}
                  <span className="text-[#FFD166] text-xl leading-none shrink-0 group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="mt-3 text-[#B9C8D5] text-sm leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        {/* Related */}
        <section className="container py-8">
          <h2 className="font-serif text-2xl text-[#F6F1E4] mb-5">Related rentals near Disney World</h2>
          <div className="flex flex-wrap gap-3">
            {[
              { label: "Crib rental Disney World", to: "/crib-rental-disney-world" },
              { label: "Baby Brezza rental Orlando", to: "/baby-brezza-rental-orlando" },
              { label: "Stroller rentals Disney World", to: "/disney-world-stroller-rentals" },
              { label: "Last-minute stroller rental", to: "/last-minute-stroller-rental-disney-world" },
              { label: "Orlando stroller rentals", to: "/stroller-rentals-orlando" },
            ].map((l) => (
              <Link key={l.to} to={l.to} className="text-sm text-[#FFD166] border border-[#FFD166]/25 px-4 py-2 rounded-full hover:bg-[#FFD166]/10 transition-colors">{l.label}</Link>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="container pb-20 text-center">
          <div className="max-w-xl mx-auto rounded-2xl bg-[#0A1825] border border-[#FFD166]/20 p-8">
            <h2 className="font-serif text-2xl text-[#F6F1E4] mb-3">Reserve baby gear for your Disney trip</h2>
            <p className="text-[#B9C8D5] mb-6">Enter your dates and delivery address to see exactly what is available and secure your rental in minutes.</p>
            <Link to="/#trip-setup"><button className="btn-primary" data-testid="babygear-cta-bottom">Check Availability <ArrowRight className="inline w-4 h-4 ml-1" /></button></Link>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
